import { Link } from 'react-router-dom';

// Static Terms of Service page. Plain content — the global SiteFooter (App.js)
// still renders beneath it, so no footer here. This is a standard template and
// should be reviewed by counsel before it is relied upon.
const COMPANY = 'Bloom Products and Services, Inc.';
const EFFECTIVE_DATE = 'July 17, 2026';
const CONTACT_EMAIL = 'legal@love-bloom.com';

const Terms = () => (
    <div className="legal-page">
        <div className="legal-container">
            <Link to="/" className="legal-home">← Back to Bloom</Link>
            <h1 className="legal-title">Terms of Service</h1>
            <p className="legal-meta">Last updated: {EFFECTIVE_DATE}</p>

            <p>
                These Terms of Service (the “Terms”) govern your access to and use of the
                Bloom platform, websites, applications, and related services (together, the
                “Service”), operated by {COMPANY} (“Bloom,” “we,” “us,” or “our”). By
                accessing or using the Service, or by creating an account, you agree to be
                bound by these Terms. If you do not agree, do not access or use the Service.
            </p>

            <h2>1. The Service</h2>
            <p>
                Bloom provides a digital product passport platform for fine jewellery. Brands
                and merchants may create, issue, and enrich digital passports for their
                products; owners may view a product’s provenance, store related records (such
                as receipts, appraisals, and insurance documents), request service or repair,
                communicate with the issuing brand, and transfer ownership of a passport. We
                may add, change, or remove features of the Service at any time.
            </p>

            <h2>2. Eligibility and Accounts</h2>
            <p>
                You must be at least 18 years old and able to form a binding contract to use
                the Service. When you create an account, you agree to provide accurate
                information and to keep it up to date. You are responsible for safeguarding
                your credentials and for all activity that occurs under your account, and you
                agree to notify us promptly of any unauthorized use.
            </p>

            <h2>3. Passports, Content, and Ownership of Data</h2>
            <p>
                You retain ownership of the content you submit to the Service, including
                documents, images, and messages (“Your Content”). You grant Bloom a
                worldwide, non-exclusive, royalty-free license to host, store, reproduce, and
                display Your Content solely to operate and provide the Service. You represent
                that you have the rights necessary to submit Your Content and that it does not
                infringe the rights of any third party. What each party can see within a
                passport depends on their relationship to the product (for example, brand,
                current owner, or prior owner), as described in the Service.
            </p>

            <h2>4. Acceptable Use</h2>
            <p>
                You agree not to misuse the Service. You will not: (a) upload unlawful,
                infringing, or fraudulent content; (b) misrepresent the authenticity,
                provenance, or ownership of any product; (c) attempt to gain unauthorized
                access to the Service or other users’ data; (d) interfere with or disrupt the
                integrity or performance of the Service; or (e) use the Service to violate any
                applicable law or regulation.
            </p>

            <h2>5. Intellectual Property</h2>
            <p>
                The Service, including its software, design, and trademarks, is owned by{' '}
                {COMPANY} and its licensors and is protected by intellectual property
                laws. Except for the rights expressly granted to you in these Terms, we
                reserve all rights in and to the Service.
            </p>

            <h2>6. Third-Party Services and Links</h2>
            <p>
                The Service may integrate with or link to third-party services that we do not
                control. We are not responsible for the content, policies, or practices of any
                third party, and your use of those services is governed by their terms.
            </p>

            <h2>7. Disclaimers</h2>
            <p>
                The Service is provided “as is” and “as available,” without warranties of any
                kind, whether express or implied, including warranties of merchantability,
                fitness for a particular purpose, and non-infringement. We do not warrant that
                the Service will be uninterrupted, secure, or error-free, or that any product
                information, valuation, or provenance record is accurate or complete.
            </p>

            <h2>8. Limitation of Liability</h2>
            <p>
                To the maximum extent permitted by law, {COMPANY} and its officers, employees,
                and agents will not be liable for any indirect, incidental, special,
                consequential, or punitive damages, or for any loss of profits, data, or
                goodwill, arising out of or relating to your use of the Service. Our total
                liability for any claim arising out of or relating to these Terms or the
                Service will not exceed the greater of the amount you paid us in the twelve
                months before the claim or one hundred U.S. dollars (US$100).
            </p>

            <h2>9. Indemnification</h2>
            <p>
                You agree to indemnify and hold harmless {COMPANY} from any claims, damages,
                liabilities, and expenses (including reasonable legal fees) arising out of Your
                Content, your use of the Service, or your violation of these Terms.
            </p>

            <h2>10. Termination</h2>
            <p>
                We may suspend or terminate your access to the Service at any time, with or
                without notice, if you violate these Terms or if we discontinue the Service.
                You may stop using the Service at any time. Provisions that by their nature
                should survive termination will survive.
            </p>

            <h2>11. Governing Law</h2>
            <p>
                These Terms are governed by the laws of the State of Delaware, United States,
                without regard to its conflict-of-laws principles. The courts located in
                Delaware will have exclusive jurisdiction over any dispute arising out of or
                relating to these Terms, except where prohibited by applicable law.
            </p>

            <h2>12. Changes to These Terms</h2>
            <p>
                We may update these Terms from time to time. If we make material changes, we
                will update the “Last updated” date above and, where appropriate, provide
                additional notice. Your continued use of the Service after changes take effect
                constitutes acceptance of the revised Terms.
            </p>

            <h2>13. Contact</h2>
            <p>
                Questions about these Terms may be sent to{' '}
                <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>, {COMPANY}
            </p>
        </div>
    </div>
);

export default Terms;
